import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type ToastVariant = 'success' | 'info' | 'warning' | 'error';

interface ToastProps {
  message: string | null;
  variant?: ToastVariant;
  position?: 'top-right' | 'top-center' | 'center';
  onClose?: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-green-100 text-green-800 border border-green-400',
  info: 'bg-gray-800 text-white',
  warning: 'bg-yellow-100 text-yellow-800 border border-yellow-400',
  error: 'bg-red-100 text-red-800 border border-red-400',
};

const positionStyles = {
  'top-right': 'fixed top-4 right-4',
  'top-center': 'fixed top-4 left-1/2 transform -translate-x-1/2',
  center: 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
};

export function Toast({
  message,
  variant = 'info',
  position = 'top-right',
  onClose,
  autoClose = true,
  autoCloseDelay = 3000,
}: ToastProps) {
  // Auto-close the toast after the specified delay
  React.useEffect(() => {
    if (message && autoClose) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [message, autoClose, autoCloseDelay, onClose]);

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className={`${positionStyles[position]} ${variantStyles[variant]} px-6 py-3 rounded-md shadow-md z-50 max-w-md flex items-start`}
          role="status"
          aria-live="polite"
        >
          {/* For error/warning variants, add an icon */}
          {(variant === 'error' || variant === 'warning') && (
            <svg
              className={`flex-shrink-0 w-5 h-5 mr-3 mt-0.5 ${variant === 'error' ? 'text-red-500' : 'text-yellow-500'}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          )}

          {/* Text content with proper line wrapping */}
          <span className="text-sm break-words flex-grow">{message}</span>

          {/* Optional close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="flex-shrink-0 ml-3 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
